import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, Lock, Mail } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || "/app";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Brand mark */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center">
            <Rocket size={18} />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight">Launchpad</div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Shopify app</div>
          </div>
        </div>

        <div className="lp-card p-7" data-testid="login-card">
          <div className="lp-section-label">Sign in</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Welcome back</h1>
          <p className="text-sm text-gray-600 mt-2">
            Sign in to manage your launch campaigns.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="lp-label" htmlFor="login-email">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="login-email"
                  type="email"
                  required
                  className="lp-input pl-10"
                  data-testid="login-email-input"
                  placeholder="admin@launchpad.shop"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="lp-label" htmlFor="login-password">Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="login-password"
                  type="password"
                  required
                  className="lp-input pl-10"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="lp-btn-primary w-full justify-center"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <div className="mt-5 text-center text-xs text-gray-500">
          Need to browse the storefront?{" "}
          <Link to="/storefront" className="underline hover:text-gray-900" data-testid="login-to-storefront-link">
            Public storefront →
          </Link>
        </div>
      </div>
    </div>
  );
}
